import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import {
  Play,
  Pause,
  Download,
  Volume2,
  VolumeX,
  Loader2,
  Mic,
  RefreshCw,
} from "lucide-react";

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const CallRecordingPlayer = ({ call, compact = false }) => {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(call.recording_url || null);
  const [fetchError, setFetchError] = useState(null);

  const isExotelCall =
    call.call_sid &&
    !call.call_sid.startsWith("DEMO_") &&
    !call.call_sid.startsWith("MANUAL_") &&
    !call.call_sid.startsWith("FAILED_") &&
    !call.call_sid.startsWith("CALL_");

  const canFetchRecording = !recordingUrl && isExotelCall;

  const handleFetchRecording = async () => {
    setIsFetching(true);
    setFetchError(null);
    try {
      const res = await axios.get(`${API}/calls/${call.id}/recording`);
      if (res.data.recording_url) {
        setRecordingUrl(res.data.recording_url);
      } else {
        setFetchError("No recording available yet");
      }
    } catch {
      setFetchError("Failed to fetch recording");
    } finally {
      setIsFetching(false);
    }
  };

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleSeek = useCallback(
    (e) => {
      if (!audioRef.current || !duration) return;
      const rect = progressRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      audioRef.current.currentTime = pct * duration;
      setCurrentTime(pct * duration);
    },
    [duration]
  );

  const handleDownload = useCallback(() => {
    if (!recordingUrl) return;
    const a = document.createElement("a");
    a.href = recordingUrl;
    a.download = `recording-${call.call_sid || call.id}.mp3`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [recordingUrl, call]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
    };
    const onCanPlay = () => setIsLoaded(true);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("canplay", onCanPlay);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("canplay", onCanPlay);
    };
  }, [recordingUrl]);

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  // No recording and can't fetch - show nothing
  if (!recordingUrl && !canFetchRecording) return null;

  // Show fetch button
  if (!recordingUrl && canFetchRecording) {
    return (
      <div
        className={`flex items-center gap-2 ${compact ? "" : "mt-2"}`}
        data-testid={`recording-fetch-${call.id}`}
      >
        <Button
          size="sm"
          variant="outline"
          onClick={handleFetchRecording}
          disabled={isFetching}
          className="h-7 text-xs border-indigo-200 text-indigo-600 hover:bg-indigo-50"
        >
          {isFetching ? (
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3 mr-1" />
          )}
          {isFetching ? "Fetching..." : "Load Recording"}
        </Button>
        {fetchError && (
          <span className="text-xs text-red-500">{fetchError}</span>
        )}
      </div>
    );
  }

  // Full player
  return (
    <div
      className={`${
        compact ? "p-2" : "p-3 mt-2"
      } rounded-lg bg-gradient-to-r from-slate-50 to-indigo-50/40 border border-slate-200`}
      data-testid={`recording-player-${call.id}`}
    >
      <audio ref={audioRef} preload="metadata" src={recordingUrl} />

      <div className="flex items-center gap-2">
        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
            isPlaying
              ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
              : "bg-white border border-slate-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300"
          }`}
          data-testid={`play-btn-${call.id}`}
        >
          {isPlaying ? (
            <Pause className="w-3.5 h-3.5" />
          ) : (
            <Play className="w-3.5 h-3.5 ml-0.5" />
          )}
        </button>

        {/* Progress bar */}
        <div className="flex-1 min-w-0">
          <div
            ref={progressRef}
            className="h-2 bg-slate-200 rounded-full cursor-pointer relative group"
            onClick={handleSeek}
            data-testid={`progress-bar-${call.id}`}
          >
            <div
              className="absolute inset-y-0 left-0 bg-indigo-500 rounded-full transition-all duration-100"
              style={{ width: `${progressPct}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-indigo-500 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progressPct}% - 6px)` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] font-mono text-slate-500">
              {formatTime(currentTime)}
            </span>
            <span className="text-[10px] font-mono text-slate-500">
              {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Volume toggle */}
        <button
          onClick={toggleMute}
          className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
          data-testid={`mute-btn-${call.id}`}
        >
          {isMuted ? (
            <VolumeX className="w-3.5 h-3.5" />
          ) : (
            <Volume2 className="w-3.5 h-3.5" />
          )}
        </button>

        {/* Download */}
        <button
          onClick={handleDownload}
          className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors flex-shrink-0"
          data-testid={`download-btn-${call.id}`}
        >
          <Download className="w-3.5 h-3.5" />
        </button>

        {/* Recording indicator */}
        {!compact && (
          <div className="flex items-center gap-1 text-[10px] text-indigo-500 font-medium flex-shrink-0">
            <Mic className="w-3 h-3" />
            REC
          </div>
        )}
      </div>
    </div>
  );
};

/** Small badge to indicate recording is available */
export const RecordingBadge = ({ hasRecording }) => {
  if (!hasRecording) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded text-[10px] font-medium border border-indigo-100"
      data-testid="recording-badge"
    >
      <Mic className="w-2.5 h-2.5" />
      REC
    </span>
  );
};
